import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ISelectItem } from '../../../store/models/select.model';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

export interface IPaginatorChangeEvent {
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
})
export class PaginatorComponent implements OnInit {
  @Input() length: number;
  @Input() pageSize: number;
  @Input() pageSizeOptions: number[];
  @Input() showFirstLastButtons: boolean;
  @Input() showPrevNextButtons: boolean;
  @Input() maxVisualPages = 3;
  @Input() currentPage = 1;
  @Input() totalCount = 0;
  @Input() showItemsPerPage = true;

  @Output() paginationChange: EventEmitter<IPaginatorChangeEvent>;

  componentReady = false;

  onChange: Subject<IPaginatorChangeEvent> =
    new Subject<IPaginatorChangeEvent>();

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
  }

  get change() {
    return this.onChange.asObservable();
  }

  getPageSize() {
    if (this.totalCount < this.pageSize) {
      return this.totalCount;
    }

    let currentPageRange = (this.currentPage * this.pageSize) - this.pageSize;

    if (currentPageRange <= 0) {
      currentPageRange = 1;
    } else {
      currentPageRange++;
    }

    let shownPages = (this.currentPage || 1) * this.pageSize;

    if (shownPages > this.totalCount) {
      shownPages = this.totalCount;
    }

    return `${currentPageRange}-${shownPages}`;
  }

  ngOnInit(): void {
    if (!this.pageSizeOptions) {
      this.pageSizeOptions = [10, 20, 50, 1000];
    }

    this.componentReady = true;
  }

  emitChanges(eventValue: IPaginatorChangeEvent): void {
    if (this.paginationChange) {
      this.paginationChange.emit(eventValue);
    }

    this.onChange.next(eventValue);
  }

  detectChanged(): void {
    this.changeDetectorRef.detectChanges();
  }

  buildPageSizeSelector(): ISelectItem[] {
    return this.pageSizeOptions.map(i => ({ label: i.toString(), value: +i }));
  }

  goToSpecificPage(page: number) {
    this.emitChanges({
      page,
      pageSize: this.pageSize,
    });
  }

  getQueryParams(target: string, pageSize: number = null) {

    if (!pageSize) {
      pageSize = this.pageSize;
    }

    return {
      ...this.activatedRoute.snapshot.queryParams,
      page: this.goToTargetPage(target, true),
      pageSize,
    };
  }

  goToTargetPage(target: string, returnOnly: boolean = false) {
    switch (target) {
      case 'first':
        if (returnOnly) {
          return 1;
        }
        this.emitChanges({
          page: 1,
          pageSize: this.pageSize,
        });
        break;
      case 'last':
        if (returnOnly) {
          return this.length;
        }
        this.emitChanges({
          page: this.length,
          pageSize: this.pageSize,
        });
        break;
      case 'next':
        if (this.currentPage < this.length) {
          if (returnOnly) {
            return this.currentPage + 1;
          }
          this.emitChanges({
            page: this.currentPage + 1,
            pageSize: this.pageSize,
          });
        }
        break;
      case 'prev':
        if (this.currentPage > 1) {
          if (returnOnly) {
            return this.currentPage - 1;
          }
          this.emitChanges({
            page: this.currentPage - 1,
            pageSize: this.pageSize,
          });
        }
    }
  }

  goToPage(page: string | number) {
    if (typeof page === 'number') {
      return this.goToSpecificPage(page);
    }

    this.goToTargetPage(page);
  }

  fromArrayOfNumbers(num: number) {
    return new Array(num).fill('').map((i, index) => index);
  }

  getShownPagesNumber(): number[] {
    let result: number[] = [];

    if (!this.length) {
      return result;
    }

    const middlePage = Math.round(this.maxVisualPages / 2);
    let index = 0;

    do {
      const target = this.currentPage - middlePage + index;
      index++;

      if (target <= 0) {
        continue;
      }

      if (this.currentPage - middlePage === target + 1) {
        continue;
      }

      if (target > this.length) {
        result = [];
        index = -2;
      }

      result.push(target);
    } while (result.length !== this.maxVisualPages);

    return result;
  }

  onPageSizeSelection($event: number) {
    this.emitChanges({
      page: 1, // fallback to the first page!!
      pageSize: $event,
    });
  }

  getPaginationOptions(): number[] {
    return this.pageSizeOptions;
  }
}
